import { createFileRoute, Link } from "@tanstack/react-router";
import { pageHead } from "@/lib/seo";
import { PageLayout, PageSection } from "@/components/site/PageLayout";
import { Reveal, Label } from "@/components/site/Reveal";
import { ldScript, serviceSchema } from "@/lib/schema";
import heroImg from "@/assets/certification-blueprints.jpg";

export const Route = createFileRoute("/product-certification-consulting")({
  head: () => ({
    ...pageHead({
      title: "Product Approval & NOA Consulting · SunSmart Engineering",
      description:
        "Florida Product Approval, Miami-Dade NOA and Texas Department of Insurance submittals for windows, doors, roofing, structural components and solar equipment.",
      path: "/product-certification-consulting",
    }),
    scripts: [
      ldScript(
        serviceSchema({
          name: "Product Certification Consulting",
          description:
            "Certification and approval consulting for building envelope and solar products, including Florida Product Approval, Miami-Dade NOA, and Texas Department of Insurance submittals.",
          url: "/product-certification-consulting",
          serviceType: "Certification Consulting",
        }),
      ),
    ],
  }),
  component: ProductCertificationPage,
});

const specialties = [
  {
    n: "01",
    title: "Code Compliance",
    items: [
      "International Building Code (IBC)",
      "State-Level Building Codes (e.g., Florida Building Code)",
      "National Electric Code (NEC)",
      "National Fire Protection Association (NFPA)",
    ],
  },
  {
    n: "02",
    title: "Feasibility Studies",
    items: [
      "UL Certification",
      "Florida Product Approval",
      "Miami-Dade Notice of Acceptance (NOA)",
      "Texas Department of Insurance (TDI) Approval",
      "SRCC Certification",
    ],
  },
  {
    n: "03",
    title: "Product Types",
    items: [
      "Windows, Doors & Skylights",
      "Roofing Systems & Components",
      "Structural Components & Connectors",
      "Shutters, Impact Protection & Exterior Attachments",
      "Racking & Roof Attachments",
      "PV Modules & Inverters",
      "Battery Energy Storage Systems (ESS)",
      "Balance of System (BOS) Equipment such as enclosures and wire management",
    ],
  },
];

const process = [
  { n: "01", k: "Initial Consultation", v: "Start with a personalized consultation to discuss your objectives and specific needs for entering the U.S. market." },
  { n: "02", k: "Application and Screening", v: "Submit an application detailing your product specifications and target market. Our team reviews your submission for alignment with U.S. market standards." },
  { n: "03", k: "Product Shipment", v: "Upon approval, ship your product to our facilities. We handle every product with care and confidentiality." },
  { n: "04", k: "Product Testing", v: "Our experts conduct extensive testing in controlled environments and real-world settings to assess performance, durability, and efficiency." },
  { n: "05", k: "Regulatory Compliance & Market Path Analysis", v: "We analyze the necessary steps for compliance and certification, guiding you through the complexities of U.S. solar regulations." },
  { n: "06", k: "Reporting and Feedback", v: "Receive a detailed report with findings, recommendations, and a clear roadmap for market entry." },
  { n: "07", k: "Revision and Resubmission", v: "Should any issues arise, we offer specific guidance on adjustments needed for product revision and resubmission." },
  { n: "08", k: "Follow-Up and Continuous Support", v: "Post-evaluation, we assist with implementing recommendations, further testing, and connecting with distributors and industry partners." },
];

function ProductCertificationPage() {
  return (
    <PageLayout
      kicker="Construction Services · Certification"
      title="Product Certification Consulting"
      intro="We help manufacturers get products approved and into the U.S. market, from windows, doors, and roofing systems that form or attach to the building envelope through solar racking, modules, and storage equipment. Whether you are an international manufacturer navigating U.S. regulations or a domestic producer expanding reach, we handle the testing path, the calculations, and the submittals."
    >
      {/* WHAT WE SPECIALIZE IN */}
      <PageSection label="01" title="What We Specialize In">
        <div className="grid gap-5 lg:grid-cols-3">
          {specialties.map((s, i) => (
            <Reveal key={s.title} delay={i * 100}>
              <div className="h-full rounded-sm border border-border bg-surface p-8">
                <div className="font-display text-4xl font-black leading-none text-sun">{s.n}</div>
                <div className="mt-4 font-display text-lg font-bold uppercase">{s.title}</div>
                <ul className="mt-4 flex flex-col gap-2">
                  {s.items.map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sun" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* BUILDING ENVELOPE APPROVALS */}
      <PageSection label="02" title="Building Envelope Approvals">
        <Reveal>
          <p className="max-w-3xl text-lg leading-relaxed text-muted-foreground">
            Any product that forms part of the building envelope, or gets mounted to it, needs
            approval before it can be specified and installed. We prepare and manage the
            submittals that get you listed, including the engineering evaluations and
            calculations that back them up.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-px border border-border bg-border sm:grid-cols-3">
          {[
            {
              title: "Florida Product Approval",
              desc: "Statewide approval through the Florida Building Commission, backed by test reports or engineering evaluation reports we prepare and seal.",
            },
            {
              title: "Miami-Dade NOA",
              desc: "Notice of Acceptance submittals for the High Velocity Hurricane Zone, the most demanding approval in the country and a strong differentiator nationally.",
            },
            {
              title: "Texas Dept. of Insurance",
              desc: "TDI product evaluation submittals for windstorm-designated catastrophe areas along the Texas coast.",
            },
          ].map((a, i) => (
            <Reveal key={a.title} delay={i * 100}>
              <div className="h-full bg-background p-8">
                <div className="font-display text-lg font-bold uppercase text-sun">{a.title}</div>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{a.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>


      {/* HOW IT WORKS */}
      <PageSection label="03" title="How It Works" theme="surface">
        <div className="grid gap-4 md:grid-cols-2">
          {process.map((s, i) => (
            <Reveal key={s.n} delay={(i % 4) * 90}>
              <div className="flex gap-6 rounded-sm border border-border bg-background p-6">
                <div className="font-display text-4xl font-black leading-none text-sun">{s.n}</div>
                <div>
                  <div className="font-display text-base font-bold uppercase leading-tight">{s.k}</div>
                  <p className="mt-1 text-sm text-muted-foreground leading-relaxed">{s.v}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </PageSection>

      {/* ADDITIONAL SUPPORT */}
      <PageSection label="04" title="Additional Support">
        <div className="grid gap-16 lg:grid-cols-[1fr_1fr] lg:items-center">
          <Reveal>
            <div className="overflow-hidden rounded-sm border border-border">
              <img
                src={heroImg}
                alt="Architectural blueprints on a desk with drafting tools"
                loading="lazy"
                className="aspect-[4/3] h-full w-full object-cover"
              />
            </div>
          </Reveal>
          <div className="grid gap-5">
            <Reveal delay={80}>
              <div className="rounded-sm border border-border bg-surface p-8">
                <div className="font-display text-lg font-bold uppercase text-sun">Product Development Support</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  We help bring your product to life by creating accurate 3D models, extrusion die
                  drawings, and other documentation to check manufacturing feasibility, create rapid
                  prototypes, and test fit. Our team provides:
                </p>
                <ul className="mt-4 flex flex-col gap-2">
                  {["Product improvement analysis", "3D modeling", "Manufacturing drawings"].map((item) => (
                    <li key={item} className="flex gap-3 text-sm text-muted-foreground leading-relaxed">
                      <span className="mt-2 h-1 w-1 shrink-0 rounded-full bg-sun" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={160}>
              <div className="rounded-sm border border-border bg-surface p-8">
                <div className="font-display text-lg font-bold uppercase text-sun">Regulatory Approvals</div>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">
                  Certain products are eligible for Florida Product Approval, Miami-Dade NOA, or
                  Texas Department of Insurance approval. Achieving these certifications sets your
                  product apart, providing a level of quality and credibility others can't match.
                  SunSmart Engineering assists in meeting these requirements and obtaining approvals.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </PageSection>

      {/* GET STARTED TODAY */}
      <PageSection label="05" title="Get Started Today">
        <div className="grid gap-16 lg:grid-cols-12 lg:items-end">
          <Reveal className="lg:col-span-8">
            <p className="text-lg leading-relaxed text-muted-foreground">
              Contact us to schedule your initial consultation and take the first step toward
              successful market entry. At SunSmart Engineering, we are committed to helping you
              bring your product to market.
            </p>
          </Reveal>
          <Reveal delay={100}>
            <div className="lg:col-span-4 lg:text-right">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-full bg-sun px-7 py-4 text-sm font-semibold text-ink hover:scale-[1.03] transition-transform"
              >
                Get started <span>→</span>
              </Link>
            </div>
          </Reveal>
        </div>

        {/* related services */}
        <div className="mt-16 border-t border-border pt-10">
          <Label n="→" text="Related Services" />
          <div className="mt-6 flex flex-wrap gap-4">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              All Services
            </Link>
            <Link
              to="/pv"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Solar Electric (PV) Design
            </Link>
            <Link
              to="/energy-storage"
              className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-6 py-3 text-sm font-semibold text-foreground hover:border-sun hover:text-sun transition-colors"
            >
              Energy Storage Design
            </Link>
          </div>
        </div>
      </PageSection>
    </PageLayout>
  );
}
